import React, { forwardRef, useState, useCallback, useMemo, useRef, useImperativeHandle, useContext, useEffect, memo } from "react";
import PropTypes from "prop-types";
import {
  Grid,
  GridColumn,
  GridToolbar,
} from "@progress/kendo-react-grid";
import { groupBy } from '@progress/kendo-data-query';
import {
  eachDeep,
  isDateType,
  isUserFilterActive,
  addHistoryFilter,
  getPrimaryField,
} from "../utils/helper";
import { saveAs } from 'file-saver';
import eventEmitter from "../utils/eventEmitter";
import LoadingPanel from "../LoadingPanel";
import DialogSort from "./dialogs/DialogSort";
import ConfirmDialog from "../dialogs/ConfirmDialog";
import FileDialog from "../dialogs/FileDialog";
import ReportDialog from "../dialogs/ReportDialog";
import Dialog from "../dialogs/Dialog";
import Checkbox from '../forms/Checkbox';
import {
  processFilter,
  buildWidth,
  buildFilter,
  buildMinResizableWidth,
  buildResetText,
  buildFormat,
  prepareFilter,
  getFormType,
  getColumn,
  getField,
  getSelectedItems,
  getGridSelected,
} from "./gridHelper";

import Toolbar from "./Toolbar";
import ColumnMenu from "./ColumnMenu";
import SelectionMenu from "./SelectionMenu";
import FilterDialog from "./dialogs/DialogFilterExtended";
import _ from "lodash";
import { DataGridProvider } from "./GridDataContext";

import { MetaContext } from "../utils/withMeta";

import flags from "./flags";
import { useStorage } from "../utils/withStorage";
import { useDidMount, usePrevious } from "../utils/hooks";

import HeaderCell from "./cells/HeaderCell";
import { editBooleanCell } from './cells/EditBooleanCell';
import { DefaultCell } from "./cells/DefaultCell";
import { editBooleanCheckCell } from './cells/EditBooleanCheckCell';
import { editTextCell } from './cells/EditTextCell';
import { editNumericCell } from './cells/EditNumericCell';
import { editDropdownCell } from './cells/EditDropdownCell';
import { editPickerCell } from './cells/EditPickerCell';
import { editMultiPickerCell } from './cells/EditMultiPickerCell';
import { editDateTimeCell } from './cells/EditDateTimeCell';
import { editDateRangeCell } from './cells/EditDateRangeCell';
import { editDateRangeInputCell } from './cells/EditDateRangeInputCell';
import { fileCell } from './cells/FileCell';
import { editPhoneNoCell } from './cells/EditPhoneNoCell';
import { editRCInputCell } from "./cells/EditRCInputCell";
import { readMultiCell } from './cells/ReadMultiCell';
import { useLocation, useNavigate } from "react-router";
import "./Grid.scss";
import DropdownFilterCell from "./cells/DropdownFilterCell";
import DateFilterCell from "./cells/DateFilterCell";
import StringNumericFilterCell from "./cells/StringNumericFilterCell";
import CompactDateFilterCell from "./cells/CompactDateFilterCell";
import CompactStringNumericFilterCell from "./cells/CompactStringNumerciFilterCell";

const FILTER_OPERATORS = {
  text: [
    { text: 'grid.filterContainsOperator', operator: 'contains' },
    { text: 'grid.filterNotContainsOperator', operator: 'doesnotcontain' },
    { text: 'grid.filterEqOperator', operator: 'eq' },
    { text: 'grid.filterNotEqOperator', operator: 'neq' },
    { text: 'grid.filterStartsWithOperator', operator: 'startswith' },
    { text: 'grid.filterEndsWithOperator', operator: 'endswith' },
    { text: 'grid.filterIsEmptyOperator', operator: 'isempty' },
    { text: 'grid.filterIsNotEmptyOperator', operator: 'isnotempty' }
  ],
  numeric: [
    { text: 'grid.filterEqOperator', operator: 'eq' },
    { text: 'grid.filterNotEqOperator', operator: 'neq' },
    { text: 'grid.filterGteOperator', operator: 'gte' },
    { text: 'grid.filterGtOperator', operator: 'gt' },
    { text: 'grid.filterLteOperator', operator: 'lte' },
    { text: 'grid.filterLtOperator', operator: 'lt' },
    { text: 'grid.filterIsNullOperator', operator: 'isnull' },
    { text: 'grid.filterIsNotNullOperator', operator: 'isnotnull' },
    { text: 'grid.filterIsInListOperator', operator: 'in' },
    { text: 'grid.filterIsNotInListOperator', operator: 'notin' }
  ],
  date: [
    { text: 'grid.filterEqOperator', operator: 'eq' },
    { text: 'grid.filterNotEqOperator', operator: 'neq' },
    { text: 'grid.filterAfterOrEqualOperator', operator: 'gte' },
    { text: 'grid.filterAfterOperator', operator: 'gt' },
    { text: 'grid.filterBeforeOperator', operator: 'lt' },
    { text: 'grid.filterBeforeOrEqualOperator', operator: 'lte' },
    { text: 'grid.filterIsNullOperator', operator: 'isnull' },
    { text: 'grid.filterIsNotNullOperator', operator: 'isnotnull' },
    { text: 'grid.filterBetweenOperator', operator: 'between' }
  ],
  boolean: [
    { text: 'grid.filterBooleanAll', operator: '' },
    { text: 'grid.filterIsTrue', operator: true },
    { text: 'grid.filterIsFalse', operator: false }
  ]
};

export const ENUM_DEFAULT = "-";
const PAGE = { skip: 0, take: 5 };
const FILTER = { filters: [] };

let _lastGet = Date.now();
let _lastID;
let _timer;
let _column;
let _reselect = false;
let _lastRowClick;

/**
 * DataGrid component
 * 
 * @component
 */
export const DataGridComponent = forwardRef(function DataGrid(props, ref) {

  const {
    actions,
    args,
    allColumns,
    componentFactory,
    cancelText,
    customToolbarBreadcrumb,
    columnVirtualization = false,
    contextMenu,
    className: propsClassName,
    cellRender,
    createFilter,
    customPrintProps,
    data,
    db,
    deleteFilter,
    deleteText,
    detail,
    detailExpand,
    disabled,
    edit,
    editable,
    extendedFilter = true,
    extendedBuildColumns,
    filter,
    filterCellRender,
    filterFactory,
    fixedFilter,
    frms: propsFrms,
    getDefaultUserId,
    handleChangeFrm,
    handleGetFilters,
    handleKeyDown,
    handleReset,
    hidden,
    hiddenBack,
    initFilter,
    initSort,
    isDataChanged,
    isLoading,
    isValueChanged,
    managedUsers,
    multiple,
    navigatable,
    noMeta,
    onAbortChangesAll,
    onAdd: propsOnAdd,
    onCancel,
    onChange,
    onChangeField,
    onChangeSchema,
    onCopy: propsOnCopy,
    onCopyAdditional,
    onDelete,
    onEditChange,
    onExpandChange,
    onFilter,
    onGet,
    onGetList,
    onGetSchema,
    onItemChange,
    onPrint,
    onRefresh,
    onRowDoubleClick,
    onSelect,
    onUpdateAll,
    page,
    parentProps,
    predefinedFilter,
    primaryField,
    printItems,
    reducer,
    rememberSelection,
    rowProps,
    schema,
    scrollable,
    selectable: propsSelectable,
    selected = [],
    selectedFrm,
    showAbort,
    showMsg,
    showToolbarButtons,
    sort,
    style,
    tlac,
    toolbarActions,
    total,
    updateFilter,
    updateSort,
    userFilterExtended,
    userFilterList,
    validationAlert,
    withMeta = true,
  } = props;
  const dataPrimaryField = getPrimaryField(primaryField);
  const frms = useMemo(() => (propsFrms || []), [propsFrms]);

  const isHidden = useCallback((flag) => (!!(hidden & flag)), [hidden]);
  const isDisabled = useCallback((flag) => (!!(disabled & flag)), [disabled]);
  const isHiddenORDisabled = useCallback((flag) => (isHidden(flag) || isDisabled(flag)), [isHidden, isDisabled]);

  const meta = useContext(MetaContext);
  const storage = useStorage();
  const location = useLocation();
  const navigate = useNavigate();

  const [initLoading, setInitLoading] = useState(true);
  const [isGetPending, setIsGetPending] = useState(false);

  const [showFilter, setShowFilter] = useState(() => (filter?.filters?.filter(x => x.filterType !== 1) || []));
  const [gridFilter, setGridFilter] = useState(() => ({
    ...filter,
    filters: showFilter,
  }));
  useEffect(() => {
    const newShowFilter = filter?.filters?.filter(x => x.filterType !== 1) || [];
    setShowFilter(newShowFilter);
    setGridFilter({
      ...filter,
      filters: newShowFilter,
    });
  }, [filter]);
  // const [fastFilter, setFastFilter] = useState(FILTER);

  const [filterable, setFilterable] = useState(!isHiddenORDisabled(flags.FILTER));
  const [isOpenFilter, setIsOpenFilter] = useState(false);
  const [isOpenSort, setIsOpenSort] = useState(false);
  const [isOpenReset, setIsOpenReset] = useState(false);
  const [resetValue, setResetValue] = useState(false);
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [isOpenCancel, setIsOpenCancel] = useState(false);
  const [isOpenSortWarning, setIsopenSortWarning] = useState(false);
  const [sortWarningChecked, setSortWarningChecked] = useState(false);

  const [selectedFile, setSelectedFilte] = useState(null);
  const [report, setReport] = useState({ isOpen: false, blob: null, fileName: null });
  const [gridSize, setGridSize] = useState(null);

  const gridRef = useRef(null);

  const dataRef = useRef(data);
  dataRef.current = data;

  const prevData = usePrevious(data);

  const selectedRef = useRef(selected);

  const propsRef = useRef(props);
  propsRef.current = props;

  const selectedAllRef = useRef({});
  const setSelectedAll = useCallback((newSelectedAll) => { selectedAllRef.current = newSelectedAll; }, []);

  const [updateSelected, setUpdateSelected] = useState(0);
  const pendingSelectedRef = useRef(null);
  const gridSelectedRef = useRef({});
  if (pendingSelectedRef.current && !_.isEqual(selectedRef.current, selected)) {
    pendingSelectedRef.current = null;
  }
  selectedRef.current = selected;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const gridSelected = useMemo(() => pendingSelectedRef.current || getGridSelected(selected, dataPrimaryField), [updateSelected, selected, dataPrimaryField]);
  gridSelectedRef.current = gridSelected;

  const [group, setGroup] = React.useState(args?.group || []);
  const handleGroupChange = React.useCallback(event => { setGroup(event.group); }, []);

  const gridTestRef = useRef(null);

  useEffect(() => {
    if (gridTestRef.current && gridTestRef.current.element && props.name) {
      gridTestRef.current.element.setAttribute('data-testid', props.name);
    }
  }, [props.name])

  const pageable = useMemo(() => ({
    info: true,
    type: "input",
    pageSizes: [5, 10, 25, 50, 100],
    previousNext: true,
  }), []);
  const selectable = useMemo(() => ({
    cell: false,
    drag: false,
    enabled: true,
    mode: multiple ? "multiple" : "single",
    ...propsSelectable
  }), [propsSelectable, multiple]);
  const sortable = useMemo(() => ({
    allowUnsort: true,
    mode: "multiple",
  }), []);

  const getColumns = useCallback(() => {
    let columns = [];
    eachDeep(schema, null, (item, parent) => {
      if (item.filter !== 'group' && item.field && (!parent || !['container', 'multipicker', 'enum', 'radio', 'formdatatable'].includes(parent.filter))) {
        columns.push(item);
      }
    });
    return columns;
  }, [schema]);
  const columns = useMemo(() => getColumns(), [getColumns]);

  const changeTab = useCallback(() => {
    if (!isHidden(flags.DETAIL)) {
      eventEmitter.changeTab(primaryField, 1);
    }
  }, [isHidden, primaryField]);

  const handleFileClick = useCallback((field, dataItem) => {
    _column = columns.find(x => x.field === field);
    setSelectedFilte(dataItem);
  }, [columns]);

  //#region components
  const EditBooleanCheckCell = useMemo(() => editBooleanCheckCell({ isValueChanged }), [isValueChanged]);
  const EditPicker = useMemo(() => editPickerCell({ getColumns: getColumns, withMeta: withMeta, isValueChanged }), [getColumns, withMeta, isValueChanged]);
  const EditMultiPicker = useMemo(() => editMultiPickerCell({ getColumns: getColumns, withMeta: withMeta, isValueChanged }), [getColumns, withMeta, isValueChanged]);
  const EditDropdownCell = useMemo(() => editDropdownCell({ getColumns: getColumns, isValueChanged }), [getColumns, isValueChanged]);
  const EditDateTimeCell = useMemo(() => editDateTimeCell({ getColumns: getColumns, isValueChanged }), [getColumns, isValueChanged]);
  const EditDateRangeCell = useMemo(() => editDateRangeCell({ isValueChanged }), [isValueChanged]);
  const EditDateRangeInputCell = useMemo(() => editDateRangeInputCell({ isValueChanged }), [isValueChanged]);
  const EditTextCell = useMemo(() => editTextCell({ isValueChanged }), [isValueChanged]);
  const EditNumericCell = useMemo(() => editNumericCell({ getColumns: getColumns, isValueChanged }), [getColumns, isValueChanged]);
  const EditBooleanCell = useMemo(() => editBooleanCell({ getColumns: getColumns, isValueChanged }), [getColumns, isValueChanged]);
  const FileCell = useMemo(() => fileCell({ onClick: handleFileClick, isValueChanged }), [handleFileClick, isValueChanged]);
  const EditPhoneNoCell = useMemo(() => editPhoneNoCell({ isValueChanged }), [isValueChanged]);
  const ReadMultiCell = useMemo(() => readMultiCell({ getColumns: getColumns, isValueChanged }), [getColumns, isValueChanged]);
  const EditRCInputCell = useMemo(() => editRCInputCell({ isValueChanged }), [isValueChanged]);
  //#endregion

  //#region get
  const getList = useCallback(async () => {
    await onGetList();
  }, [onGetList]);

  const handleGet = useCallback(async (id, fixedFilter = null) => {
    setIsGetPending(true);
    if (allColumns) {
      let val;
      if (id === 0) {
        val = null;
      }
      else {
        val = { Data: dataRef.current.find(x => x[dataPrimaryField] === id) };
      }
      await onGet(val);
    }
    else {
      await onGet(id, fixedFilter);
    }
    setIsGetPending(false);
  }, [onGet, allColumns, dataPrimaryField]);
  //#endregion

  //#region eventEmitter 
  const handleChangePage = (page) => {
    if (!_.isEqual(page, propsRef.current.page)) {
      onPageChange({ page });
      eventEmitter.changeTab(primaryField, 0);
    }
  };

  const handleChangeFilterable = useCallback((filterable) => {
    if (!isHiddenORDisabled(flags.FILTER) && filterable.filterable !== null) {
      setFilterable(filterable.filterable);
    }
  }, [isHiddenORDisabled]);

  const handleChangeSelected = ({ name, selected }) => {
    if (name === dataPrimaryField) {
      const newGridSelected = getGridSelected(selected, dataPrimaryField);
      handleSelect(newGridSelected, false);
    }
  };

  const handleChangeGridSize = (size) => {
    setGridSize(size);
  };
  //#endregion eventEmitter

  //#region init
  const getPage = useCallback(async () => {
    let newPage = await storage.getPage();
    if (page && isHiddenORDisabled(flags.PAGING)) { return page; }
    if (newPage) {
      newPage.skip = 0;
      return newPage;
    }
    else {
      return PAGE;
    }
  }, [storage, page, isHiddenORDisabled]);

  const getFilter = useCallback(async () => {
    if (propsRef.current.filter && propsRef.current.filter.filters.length > 0) {
      return processFilter(propsRef.current.filter, fixedFilter);
    }

    const newFilter = initFilter || FILTER;
    return processFilter(newFilter, fixedFilter);
  }, [fixedFilter, initFilter]);

  const getFilterable = useCallback(async () => {
    const filterable = await storage.getFilterable();
    handleChangeFilterable({ filterable });
  }, [storage, handleChangeFilterable]);

  const getGridSize = useCallback(async () => {
    const newGridSize = await storage.getGridSize();
    setGridSize(newGridSize);
  }, [storage]);

  const initDetail = useCallback(() => {
    try {
      const urlParams = new URLSearchParams(location.search);
      const id = urlParams.get('id');
      if (id && !allColumns) {
        handleGet(id);
        changeTab();
        urlParams.delete('id');
        urlParams.size > 0 ? navigate(`${location.pathname}?${urlParams.toString()}`) : navigate(location.pathname);
      }
    } catch { }
    if (args && args?.addOnInit) {
      gridRef?.current?.querySelector('#grid-toolbar>.left>div>div>button.k-button-flat-primary')?.click();
    }
  }, [allColumns, handleGet, changeTab, navigate, location.search, location.pathname, args]);

  const init = useCallback(async () => {
    const newPage = await getPage();
    const newFilter = await getFilter();
    if (onChange) { onChange(newFilter, initSort || propsRef.current.sort || [], newPage); }
    getList();
    getFilterable();
    getGridSize();
    initDetail();
    setInitLoading(false);
  }, [getPage, getFilter, onChange, getList, getGridSize, getFilterable, initDetail, initSort]);

  useDidMount(() => {
    eventEmitter.on('CHANGE_PAGE', handleChangePage);
    eventEmitter.on('CHANGE_FILTERABLE', handleChangeFilterable);
    eventEmitter.on('CHANGE_SELECTED', handleChangeSelected);
    eventEmitter.on('CHANGE_GRIDSIZE', handleChangeGridSize);
    init();
  });

  useEffect(() => {
    return () => {
      eventEmitter.removeListener('CHANGE_PAGE', handleChangePage);
      eventEmitter.removeListener('CHANGE_FILTERABLE', handleChangeFilterable);
      eventEmitter.removeListener('CHANGE_SELECTED', handleChangeSelected);
      eventEmitter.removeListener('CHANGE_GRIDSIZE', handleChangeGridSize);
    };
  }, []); // eslint-disable-line
  //#endregion

  //#region dialogs
  const handleOpenSort = useCallback(() => { setIsOpenSort(true); }, []);
  const handleOpenDelete = useCallback(() => { setIsOpenDelete(true); }, []);
  const handleCloseDelete = useCallback(() => { setIsOpenDelete(false); }, []);
  const handleOpenCancel = useCallback(() => { setIsOpenCancel(true); }, []);
  const handleCloseCancel = useCallback(() => { setIsOpenCancel(false); }, []);
  const handleCloseReportDialog = useCallback(() => { setReport({ isOpen: false, blob: null, fileName: null }); }, []);
  const handleOpenSortWarning = useCallback(() => { setIsopenSortWarning(true); }, []);
  const handleCloseSortWarning = useCallback(() => { setIsopenSortWarning(false); }, []);
  const handleConfirmSortWarning = useCallback(() => {
    localStorage.setItem('sortWarningChecked', sortWarningChecked);
    handleCloseSortWarning();
  }, [handleCloseSortWarning, sortWarningChecked]);
  //#endregion dialogs

  //#region select
  const handleSelect = useCallback(async (newSelected, isCopy) => {
    let updateSelect = false;
    if (!_.isEqual(newSelected, gridSelectedRef.current)) {
      pendingSelectedRef.current = newSelected;
      setUpdateSelected(prev => prev + 1);
      updateSelect = true;
    }

    setTimeout(async () => {
      const selectedItems = getSelectedItems(newSelected, dataRef.current, dataPrimaryField);
      if (onSelect && updateSelect) {
        await onSelect(selectedItems);
      }

      if (selectedItems.length === 1) {
        const id = selectedItems[0][dataPrimaryField];
        const lastGet = Date.now();
        if (id !== _lastID || lastGet - _lastGet > 500) {
          _lastID = id;
          _lastGet = lastGet;
          await handleGet(id);
        }
      }
      else {
        if (!isCopy && _lastID !== 0) {
          _lastID = 0;
          handleGet(0);
        }
      }
    }, 0);
  }, [dataPrimaryField, handleGet, onSelect]);

  const clearSelected = useCallback(() => { handleSelect([], false); }, [handleSelect]);

  const prepareRememberSelection = useCallback(async () => {
    if (rememberSelection) {
      setSelectedAll({ ...selectedAllRef.current, ...gridSelectedRef.current });
      await handleSelect([], false);
      _reselect = true;
    }
    else {
      await handleSelect([], false);
    }

  }, [rememberSelection, handleSelect, setSelectedAll]);

  const buttonSelection = useCallback((action) => {
    switch (action) {
      case 0:
        const all = Object.fromEntries(dataRef.current.map(p => [p[dataPrimaryField], true]));
        handleSelect(all, false);
        break;
      case 1:
        handleSelect([], false);
        break;
      default:
        break;
    }
  }, [handleSelect, dataPrimaryField]);

  const onHeaderSelectionChange = useCallback((event) => {
    handleSelect(event.select, false);
  }, [handleSelect]);

  const onSelectChange = useCallback(async (event) => {
    handleSelect(event.select, false);
  }, [handleSelect]);
  //#endregion

  //#region change
  const handleChange = useCallback((newFilter, newSort, newPage) => {
    if (onChange) { onChange(newFilter || propsRef.current.filter, newSort || propsRef.current.sort, newPage || propsRef.current.page); }
    getList();
  }, [onChange, getList]);

  const onSortChange = useCallback((event) => {
    if (!isHiddenORDisabled(flags.SORT)) {
      const newSort = event.sort;
      const sortWarningChecked = JSON.parse(localStorage.getItem('sortWarningChecked'));

      if (newSort.length > 3 && !sortWarningChecked) {
        handleOpenSortWarning();
      }

      prepareRememberSelection();
      handleChange(null, newSort, null);
      updateSort && updateSort(newSort, columns);
    }

  }, [handleChange, prepareRememberSelection, handleOpenSortWarning, updateSort, isHiddenORDisabled, columns]);

  const setPage = useCallback((event) => {
    prepareRememberSelection();
    const newPage = event.page;
    if (page.take !== newPage.take) {
      storage.setPage(newPage);
    }
    handleChange(null, null, newPage);
  }, [handleChange, prepareRememberSelection, page.take, storage]);

  const onPageChange = useCallback((event) => {
    if (event.nativeEvent instanceof InputEvent) {
      if (_timer) {
        clearTimeout(_timer);
      }
      _timer = setTimeout(() => {
        setPage(event);
      }, 500);
    }
    else {
      setPage(event);
    }
  }, [setPage]);


  const hasParens = useCallback((f) => (f.left?.includes("(") ?? false) || (f.right?.includes(")") ?? false), []);

  const onFilterChange = useCallback(async (event, timeout = true, toggle, newStateFilter) => {
    let newFilter = event?.filter ? prepareFilter(event.filter, event, columns, frms) : FILTER;
    const stateFilter = newStateFilter || gridFilter;

    const page = propsRef.current.page;
    const newPage = { ...page, skip: 0 };

    if (extendedFilter && event?.syntheticEvent) { //change in grid filter row or columnMenu
      const oldFilters = stateFilter.filters;
      //ak filter bol uložený a mal left, right musí byť zachovaný
      const newFileds = new Set(newFilter.filters.map(f => f.field));
      const deleted = oldFilters.find(f => !newFileds.has(f.field));
      if (deleted && hasParens(deleted)) {
        newFilter.filters = oldFilters;
        showMsg && showMsg("Filter, ktorý obsahuje zátvorky nemôže byť vymazaný.", "warning");
      }
    }
    else {
      const groups = _.groupBy(newFilter.filters, 'filterId');
      let sysFilters = stateFilter.filters.filter(x => x.filterType === 6);
      let filters = stateFilter.filters.filter(x => x.filterType !== 6 && stateFilter.filters.length > 0);
      let disable = toggle;

      _.keys(groups).map(filterId => {
        disable = disable && isUserFilterActive(stateFilter.filters, { filters: groups[filterId] });
        sysFilters = sysFilters.filter(x => x.filterId !== parseInt(filterId));
        filters = filters.filter(x => x.filterId !== parseInt(filterId) && x.type === 6);
        return filterId;
      });
      newFilter.filters = disable ? [...sysFilters, ...filters] : [...sysFilters, ...filters, ...newFilter.filters];
    }
    if (extendedFilter && newFilter) {
      addHistoryFilter({ ...newFilter, filters: [...newFilter.filters] }, primaryField, event?.nativeEvent);
    }

    if (onFilter) {
      onFilter(newFilter);
    }

    setShowFilter(newFilter.filters || []);
    setGridFilter(newFilter || FILTER);

    if (timeout) {
      if (_timer) {
        clearTimeout(_timer);
      }
      _timer = setTimeout(async () => {
        await prepareRememberSelection();
        handleChange(processFilter(newFilter, fixedFilter), null, newPage);
      }, 500);
    }
    else {
      await prepareRememberSelection();
      handleChange(processFilter(newFilter, fixedFilter), null, newPage, true);
    }
    //TODO check process Filter
  }, [gridFilter, columns, fixedFilter, onFilter, handleChange, prepareRememberSelection, extendedFilter, primaryField, frms, hasParens, showMsg]);
  //#endregion

  const onBack = useCallback(() => {
    const menuItem = meta.menu.find(x => x.Route === location.pathname);
    if (!menuItem) {
      navigate(-1);
    }
    const parentMenu = meta.menu.find(x => x.MenuID === menuItem.NadMenuID);
    if (parentMenu.Route) {
      navigate(parentMenu.Route);
    }
    else {
      navigate("/menu", { state: { id: menuItem.NadMenuID } });
    }
  }, [meta.menu, navigate, location.pathname]);

  const handlePrint = useCallback(async (index, name) => {
    let newFilter = processFilter(filter, fixedFilter);
    if (index <= 1) {
      const fileType = (index === 0 ? "xlsx" : "pdf");
      if (noMeta) {
        const printProps = customPrintProps && customPrintProps();
        const primaryField = printProps?.primaryField || dataPrimaryField;
        const newSort = printProps?.sort || sort;
        const selectedIds = selectedRef.current.map(item => item[primaryField])?.join(',');
        const formName = window.location.href?.split("/")?.pop();
        const parentId = printProps?.id;
        const formType = getFormType(primaryField);
        let finalFilter = printProps?.filter || filter;

        if (selectedIds) {
          finalFilter = { filters: [{ field: primaryField, operator: "in", value: selectedIds }] };
        }
        showMsg("Požiadavka bola zaregistrovaná, počkajte prosím na výsledok.");
        meta.postPrintNoMeta(primaryField, finalFilter, newSort, fileType, { Grid: columns }, formName, formType, parentId)
          .then(res => {
            if (!res) throw new Error();
            if (fileType === "pdf") {
              setReport({ isOpen: true, blob: res.blob, fileName: res.fileName });
            } else {
              saveAs(res.blob, res.fileName);
            }
          })
          .catch(error => {
            showMsg("Generovanie tlačovej zostavy zlyhalo!", "error");
            console.error(error);
          });
      } else {
        const f = filterFactory ? filterFactory(primaryField, filter) : filter;
        showMsg("Požiadavka bola zaregistrovaná, počkajte prosím na výsledok.");
        await meta.postPrint(primaryField, f, sort, fileType, { Grid: columns }, db, propsRef?.current?.fts)
          .then(res => {
            if (!res) throw new Error();;
            if (fileType === "pdf") {
              setReport({ isOpen: true, blob: res.blob, fileName: res.fileName });
            } else {
              saveAs(res.blob, res.fileName);
            }
          }).catch(error => {
            showMsg("Generovanie tlačovej zostavy zlyhalo!", "error");
            console.error(error);
          });
      }
    }

    if (onPrint && index > 1) {
      onPrint(index, name, newFilter, selectedRef.current);
    }
  }, [filter, fixedFilter, filterFactory, onPrint, meta, noMeta, primaryField, sort, columns, customPrintProps, dataPrimaryField, db, showMsg]);

  const handleTlac = useCallback(async (tlacValue, target = null) => {
    try {
      if (tlacValue?.multiFiles) {
        selectedRef.current.forEach(async item => {
          let tl = {
            ...tlacValue,
            params: {
              ...tlacValue.params,
              [primaryField]: (item[getPrimaryField(primaryField)]).toString()
            }
          };
          if (onPrint) {
            await onPrint(0, tlacValue.text, tl, target);
          }
          delete tl.dialogState;
          let res = await meta.postTlac(primaryField, tl, target);
          if (res) {
            saveAs(res.blob, res.fileName);
          }
        });
      } else {
        let tl = {
          ...tlacValue,
          params: {
            ...tlacValue.params,
            [primaryField]: selectedRef.current.map(item => item[getPrimaryField(primaryField)])?.join(',')
          }
        };
        if (tlacValue.iSposList?.includes(2)) {
          tl.params[primaryField] = dataRef.current.map(item => item[getPrimaryField(primaryField)])?.join(',');
        }
        if (onPrint) {
          await onPrint(0, tlacValue.text, tl, target);
          // setReport({ isOpen: true, blob: res.blob, fileName: res.fileName })
        }
        delete tl.dialogState;
        let res = await meta.postTlac(primaryField, tl, target);
        if (res) {
          saveAs(res.blob, res.fileName);
          if (target === "database") {
            await onGetList(propsRef.current.filter, propsRef.current.sort, propsRef.current.page, propsRef.current.primaryField);
          }
        }
      }
    } catch (error) { }
  }, [onPrint, primaryField, meta, onGetList]);

  const onReset = useCallback((value) => {
    setIsOpenReset(true);
    setResetValue(value);
  }, []);

  const handleConfirmSort = useCallback((sort) => {
    onSortChange({ sort });
    setIsOpenSort(false);
  }, [onSortChange]);

  const handleConfirmFilter = useCallback((filter, toggle) => {
    onFilterChange({ filter }, false, toggle);
    setIsOpenFilter(false);
  }, [onFilterChange]);

  const onFilterClear = useCallback(() => {
    onFilterChange({ filter: FILTER }, false, undefined, FILTER);
  }, [onFilterChange]);

  const onSwitchFilterable = useCallback(() => {
    if (!(isHiddenORDisabled(flags.FILTER))) {
      setFilterable((prev) => {
        storage.setFilterable(!prev);
        return !prev;
      });
    }
  }, [isHiddenORDisabled, storage]);

  const buildDeleteCancelText = useCallback((selected, cancel) => {
    const ids = selected && selected.map(id => ` ${id[dataPrimaryField]}`);
    const what = cancel ? "zrušiť" : "zmazať";
    const mainText = selected && selected.length > 1 ? `Určite chcete ${what} záznamy ${ids}?` : `Určite chcete ${what} záznam ${ids}?`;
    const additionalText = cancel ? cancelText : deleteText;
    return additionalText ? mainText + " " + additionalText : mainText;
  }, [dataPrimaryField, cancelText, deleteText]);

  const handleConfirmDelete = useCallback(async () => {
    if (onDelete) {
      await Promise.all(selected.map(async (item) => {
        await onDelete(item[dataPrimaryField]);
        return item + 1;
      }));
    }
    clearSelected();

    handleGet(0);
    handleCloseDelete();
    getList();
  }, [dataPrimaryField, selected, clearSelected, onDelete, handleCloseDelete, getList, handleGet]);

  const handleConfirmCancel = useCallback(async () => {
    if (onCancel) {
      await Promise.all(selected.map(async (item) => {
        await onCancel(item[dataPrimaryField]);
        return item + 1;
      }));
    }
    clearSelected();

    handleGet(0);
    handleCloseCancel();
    getList();
  }, [dataPrimaryField, selected, clearSelected, onCancel, handleCloseCancel, getList, handleGet]);

  const onSortClear = useCallback(() => { onSortChange({ sort: [] }); }, [onSortChange]);
  const handleOpenFilter = useCallback(() => { setIsOpenFilter(true); }, []);

  const handleRefresh = useCallback(() => {
    if (onRefresh) {
      onRefresh();
      return;
    }
    getList();
  }, [onRefresh, getList]);

  const onAdd = useCallback(() => {
    if (propsOnAdd) {
      propsOnAdd();
      return;
    }
    handleGet(0, fixedFilter);
    clearSelected();
    changeTab();
  }, [clearSelected, handleGet, fixedFilter, changeTab, propsOnAdd]);

  const onCopy = useCallback(async () => {
    if (propsOnCopy) {
      propsOnCopy();
      return;
    }
    handleSelect([], true);
    if (onChangeField) { onChangeField(getPrimaryField(primaryField), 0); }
    if (onCopyAdditional) {
      await onCopyAdditional();
    }
    changeTab();
  }, [handleSelect, primaryField, onChangeField, onCopyAdditional, changeTab, propsOnCopy]);

  const onColumnReorder = useCallback((event) => {
    let newColumns = _.cloneDeep(columns);
    eachDeep(newColumns, null, (item) => {
      const column = getColumn(event.columns, item);
      if (column) {
        item.order = column.orderIndex;
      }
    });
    if (onChangeSchema) onChangeSchema(newColumns);
  }, [columns, onChangeSchema]);

  const onColumnResize = useCallback((event) => {
    if (event.end) {
      let newColumns = _.cloneDeep(columns);
      eachDeep(newColumns, null, (item) => {
        const column = getColumn(event.columns, item);
        if (column && event.targetColumnId === column.field) {
          item.width = event.newWidth;
        }
      });

      if (onChangeSchema) onChangeSchema(newColumns);
    }
  }, [columns, onChangeSchema]);

  const onColumnLocked = useCallback((field, newLocked) => {
    let newColumns = _.cloneDeep(columns).map(col => {
      if (col.field === field) {
        return { ...col, locked: newLocked };
      }
      return col;
    });
    if (onChangeSchema) { onChangeSchema(newColumns); }
  }, [columns, onChangeSchema]);

  const buildCell = useCallback((props) => {
    const { tdProps, dataItem, field, onDoubleClick, onTouchStart } = props;
    const column = getColumn(columns, { field: props.field });
    const className = `${tdProps.className} ${isValueChanged && isValueChanged(dataItem, field) ? "k-state-changed" : ""}`;

    return (
      <td {...tdProps} className={className} onDoubleClick={onDoubleClick} onTouchStart={onTouchStart}>
        {column.cell(props)}
      </td>
    );
  }, [columns, isValueChanged]);

  const onDoubleClick = useCallback((e) => {
    if (onRowDoubleClick) {
      onRowDoubleClick(e);
    }
    else {
      changeTab();
    }

  }, [onRowDoubleClick, changeTab]);

  const rowRender = useCallback((rowRenderProps) => {
    const newRowProps = {
      ...rowProps && rowProps(rowRenderProps)
    };

    let className = rowRenderProps.trProps.className;
    if (newRowProps?.className) {
      className += ` ${newRowProps.className.trimStart()}`;
      delete newRowProps.className;
    }

    const Tr = <tr {...rowRenderProps.trProps} {...newRowProps} className={className} children={rowRenderProps.children} />;
    return Tr;
  }, [rowProps]); //columns, dataPrimaryField, disabled, hidden, multiple

  // reselect
  useEffect(() => {
    const reselect = () => {
      const selectedAll = selectedAllRef.current;
      const pageIds = data.map(item => item[dataPrimaryField]);
      let newSelected = {};
      Object.entries(selectedAll).forEach(([key, value]) => {
        if (pageIds.includes(Number(key))) {
          newSelected = { ...newSelected, [key]: value };
        }
      });
      handleSelect(newSelected, false);
      _reselect = false;
    };

    const isEqual = (value1, value2) => {
      const val1 = (value1 && value1.getTime ? value1.getTime() : value1);
      const val2 = (value2 && value2.getTime ? value2.getTime() : value2);
      return _.isEqual(val1, val2);
    };

    const isDataChanged = (oldData, newData) => {
      if (!oldData || !newData) {
        return false;
      }

      if (newData.length === oldData.length) {
        for (let i = 0; i < newData.length; i++) {
          for (let j = 0; j < columns.length; j++) {
            const field = columns[j].field;
            if (!isEqual(_.get(oldData[i], field), _.get(newData[i], field))) {
              return true;
            }
          }
        }
      } else return true;
      return false;
    };

    const selectedAll = selectedAllRef.current;
    if (_reselect && rememberSelection && data.length > 0 && isDataChanged(prevData, data) && Object.keys(selectedAll || {}).length > 0) {
      reselect();
    }
  }, [prevData, data, dataPrimaryField, handleSelect, selected, rememberSelection, columns]);

  const gridData = useMemo(() => {
    let res = data;
    if (args?.groupable) {
      res = groupBy(res, group);
    }
    return res;
  }, [data, args?.groupable, group]);

  useImperativeHandle(ref, () => {
    return {
      ref: gridRef.current,
      // rowClick: rowClick,
    };
  }, []);//rowClick

  const getCompactFilterCell = (filterCellRender, x) => {
    const filterType = x.filter;
    if (filterCellRender) {
      return filterCellRender;
    } else if (filterType === "enum" || filterType === "buttongroup" || filterType === "radio") {
      return (p) => DropdownFilterCell({ ...p, schemaItem: x });
    }
    else if (isDateType(filterType)) {
      return CompactDateFilterCell;
    }
    else if (filterType === "numeric") {
      return CompactStringNumericFilterCell;
    }
    return x.filterCell;
  };

  const getFilterCell = (filterCellRender, x) => {
    const filterType = x.filter;
    if (filterCellRender) {
      return filterCellRender;
    } else if (filterType === "enum" || filterType === "buttongroup" || filterType === "radio") {
      return (p) => DropdownFilterCell({ ...p, schemaItem: x });
    } else if (isDateType(filterType)) {
      return DateFilterCell;
    } else if (filterType === "numeric") {
      return StringNumericFilterCell;
    }
    return x.filterCell;
  };

  const getCellComponent = useCallback((x) => {
    if (x.cell) return buildCell;
    if (x.filter === 'boolean' && x.format === '{checkbox}') return EditBooleanCheckCell;
    if (x.filter === 'boolean') return EditBooleanCell;
    if (['enum', 'buttongroup', 'radio'].includes(x.filter)) return EditDropdownCell;
    if (x.filter === 'file') return FileCell;
    if (x.filter === 'container') return EditPicker;
    if (x.filter === 'multipicker') return EditMultiPicker;
    if (isDateType(x.filter)) return EditDateTimeCell;
    if (x.filter === 'text') return EditTextCell;
    if (x.filter === 'numeric') return EditNumericCell;
    if (x.filter === 'phoneNumber') return EditPhoneNoCell;
    if (x.filter === 'daterange') return EditDateRangeCell;
    if (x.filter === 'daterangeinput') return EditDateRangeInputCell;
    if (x.filter === '29') return EditRCInputCell;
    if (['multipicker', 'multi'].includes(x.filter)) return ReadMultiCell;
    return DefaultCell;
  }, [EditBooleanCell, EditDateRangeCell, EditRCInputCell, EditBooleanCheckCell,
    EditDateRangeInputCell, EditDateTimeCell, EditDropdownCell, EditNumericCell,
    EditPhoneNoCell, EditTextCell, FileCell, ReadMultiCell, buildCell, EditPicker, EditMultiPicker
  ]);

  const buildColumns = useCallback(() => {
    let cols = [];

    //column menu
    if (!isHidden(flags.COLUMN_MENU)) {
      cols.push(<GridColumn
        className="column-menu"
        headerClassName="column-menu-header"
        filterable={false}
        sortable={false}
        key="columnMenu"
        field=""
        resizable={false}
        reorderable={false}
        width="15px"
        locked={true}
        orderIndex={-2}
        columnMenu={(props) => (
          <ColumnMenu
            {...props}
            columns={columns}
            onColumnsSubmit={onChangeSchema}
            filterable={filterable}
            onSwitchFilterable={onSwitchFilterable}
            onColumnLocked={onColumnLocked}
          />
        )}
      />);
    }

    //selected
    if (multiple && !isHiddenORDisabled(flags.COLUMN_SELECTED)) {
      cols.push(<GridColumn
        filterable={false}
        sortable={false}
        className="selected"
        resizable={false}
        reorderable={false}
        width="50px"
        locked={true}
        orderIndex={-1}
        columnType="checkbox"
        cells={{
          headerCell: (props) => (
            <SelectionMenu
              {...props}
              actions={typeof actions === 'function' ? actions() : actions}
              disabled={isDisabled(flags.COLUMN_SELECTED)}
              buttonSelection={buttonSelection}
            />
          )
        }}
      />);
    }

    //empty schema
    if (columns.length === 0) {
      cols.push(<GridColumn
        filterable={false}
        sortable={false}
        key="empty"
        field=""
        resizable={false}
        reorderable={false}
        locked={true}
      />);
    }

    eachDeep(schema, null, (x) => {
      x.subcols = undefined;
    });

    const orderSchema = schema.sort((a, b) => a.order - b.order);
    eachDeep(orderSchema, null, (x, parent) => {
      if (!x.field || x.disabled || x.hidden || (parent && parent.filter !== 'group' && parent.schema)) return;
      const isActionOrGroup = x.filter === 'cmd' || x.filter === 'group' || x.filter === 'file';
      const buildedFilter = buildFilter(x);
      // const filterCell = filterCellRender || ((p) => FilterCell(p, x))
      const filterCell = getFilterCell(filterCellRender, x);
      const compactFilterCell = getCompactFilterCell(filterCellRender, x)

      const col = (x.filter === 'group' ?
        <GridColumn
          id={x.field}
          key={x.field}
          field={x.field}
          title={x.title}
          filter={buildedFilter}
          width={x.width}
          orderIndex={x.order}
          className={x.filter}
          filterable={x.filterable}
          sortable={x.sortable}
          editable={false}
          locked={x.locked}
        >
          {x.subcols ? x.subcols : null}
        </GridColumn>
        :
        <GridColumn
          id={x.field}
          key={x.field}
          field={getField(x)}
          title={x.title}
          filter={buildedFilter}
          format={buildFormat(x)}
          width={buildWidth(x, filterable)}
          cells={{
            headerCell: (p) => <HeaderCell {...p} schemaItem={x} />,
            filterCell: filterCell,
            data: (dataCellProps) => {
              const additionalProps = {
                onDoubleClick: (e) => {
                  onDoubleClick({
                    dataItem: dataCellProps.dataItem,
                    nativeEvent: e.nativeEvent,
                    syntheticEvent: e,
                  });
                },
                onTouchStart: (e) => {
                  const now = Date.now();
                  if (now - _lastRowClick < 500 && now - _lastRowClick > 0) {
                    onDoubleClick({
                      dataItem: dataCellProps.dataItem,
                      nativeEvent: e.nativeEvent,
                      syntheticEvent: e,
                    });
                  }
                  _lastRowClick = now;
                },
                column: x
              };
              const newCellProps = { ...dataCellProps, ...additionalProps };
              const DefaultComponent = getCellComponent(x);
              if (cellRender) {
                return cellRender(_.isFunction(DefaultComponent) ? DefaultComponent(newCellProps) : <DefaultComponent {...newCellProps} />, newCellProps);
              }
              return <DefaultComponent {...newCellProps} />;
            },
          }}
          orderIndex={x.order}
          className={x.filter + (x.required ? ' required' : '') + (x.className ? ' ' + x.className : '')}
          filterable={!isActionOrGroup && (x.filterable === false ? false : true)}
          sortable={!isActionOrGroup && (x.sortable === false ? false : true)}
          resizable={x.resizable === false ? false : true}
          reorderable={x.reorderable === false ? false : true}
          editable={x.editable}
          locked={x.locked}
          editor={buildedFilter}
          minResizableWidth={buildMinResizableWidth(x, filterable)}
          columnMenu={((x?.sortable || x?.filterable) && !filterable) && (columnProps => <ColumnMenu {...columnProps}
            compactColumnMenu={true}
            filterCell={compactFilterCell}
            columnItem={x}
            onColumnLocked={onColumnLocked}
          />)}
        />);

      if (!parent) {
        cols.push(col);
      } else {
        if (!parent.subcols) {
          parent.subcols = [];
        }
        parent.subcols.push(col);
      }
    });

    eachDeep(schema, null, (x) => {
      x.subcols = undefined;
    });

    extendedBuildColumns && cols.push(extendedBuildColumns);
    return cols;
  }, [buttonSelection, filterable,
    extendedBuildColumns, columns, actions, schema,
    onChangeSchema, isHiddenORDisabled, onSwitchFilterable,
    isDisabled, isHidden, multiple, onDoubleClick,
    onColumnLocked, filterCellRender, cellRender, getCellComponent
  ]);

  useEffect(() => {
    const handleClickFilterRow = () => {
      setIsOpenFilter(true);
    };

    const allFilterCells = document.querySelectorAll(`td[aria-label*="Filter"]`);
    allFilterCells.forEach((td) => {
      td.classList.remove("multi-filter");
      td.removeAttribute('data-filter-text');
      td.removeEventListener('click', handleClickFilterRow);
    });

    const multiFilteredFields = Object.entries(
      showFilter.reduce((acc, f) => {
        acc[f.field] = (acc[f.field] || 0) + 1;
        return acc;
      }, {})
    ).filter(([_, count]) => count > 1).map(([field]) => field);

    multiFilteredFields.forEach((field) => {
      const td = document.querySelector(`td[aria-label*="${field} Filter"]`);
      if (td) {
        td.classList.add("multi-filter");
        td.setAttribute('data-filter-text', 'Pokročilý filter');
        td.addEventListener('click', handleClickFilterRow);
      }
    });

    return () => {
      allFilterCells.forEach((td) => {
        td.removeEventListener('click', handleClickFilterRow);
      });
    };
  }, [showFilter]);

  const filterLength = useMemo(() => showFilter.length || 0, [showFilter]);

  const toolbarActionsComponent = useMemo(() => typeof toolbarActions === "function" ? toolbarActions() : toolbarActions, [toolbarActions]);

  const gridToolbar = useMemo(() => <Toolbar
    hidden={hidden}
    disabled={disabled}
    onAdd={onAdd}
    onRefresh={handleRefresh}
    onReset={onReset}
    onPrint={handlePrint}
    frms={frms}
    userFilterList={userFilterList}
    predefinedFilterList={predefinedFilter}
    filter={filter || FILTER}
    filterLength={filterLength}
    handleConfirmFilter={handleConfirmFilter}
    onFilterClear={onFilterClear}
    onSwitchFilterable={onSwitchFilterable}
    filterable={filterable}
    handleOpenSort={handleOpenSort}
    onSortClear={onSortClear}
    handleOpenFilter={handleOpenFilter}
    selectedFrm={selectedFrm}
    handleChangeFrm={handleChangeFrm}
    onDelete={handleOpenDelete}
    onCopy={onCopy}
    onCancel={handleOpenCancel}
    onTlac={handleTlac}
    onUpdateAll={onUpdateAll}
    onAbortChangesAll={onAbortChangesAll}
    printItems={printItems}
    tlac={tlac}
    showToolbarButtons={showToolbarButtons}
    customToolbarBreadcrumb={customToolbarBreadcrumb}
    reducer={reducer}
    componentFactory={componentFactory}
    hiddenBack={hiddenBack}
    onBack={onBack}
  />, [
    componentFactory, customToolbarBreadcrumb,
    hidden, disabled, filter, filterable, frms, handleChangeFrm,
    handleConfirmFilter, handleOpenCancel,
    handleOpenDelete, handleOpenFilter, handleOpenSort, handlePrint,
    handleRefresh, handleTlac, onAdd, onCopy,
    onFilterClear, onReset, onSortClear, onSwitchFilterable,
    predefinedFilter, printItems, reducer, selectedFrm,
    showToolbarButtons, tlac, userFilterList, hiddenBack,
    onAbortChangesAll, onUpdateAll,
    onBack, filterLength
  ]);

  const gridChildren = useMemo(() => [
    <GridToolbar key={"toolbar"} scrollButtons="hidden">
      {gridToolbar}
    </GridToolbar>,
    buildColumns()
  ], [buildColumns, gridToolbar]);

  const loader = useMemo(() => (
    <div>
      <LoadingPanel isLoading={true} showAbort={showAbort} />
    </div>
  ), [showAbort]);

  const rowWidth = useMemo(() => {
    switch (gridSize?.class) {
      case 'grid-small':
        return 27;
      case 'grid-large':
        return 38;
      case 'grid-medium':
      default:
        return 30;
    }
  }, [gridSize]);

  const rows = useMemo(() => (
    { data: rowRender }
  ), [rowRender]);

  return (
    <div
      ref={gridRef}
      onKeyDown={handleKeyDown}
      tabIndex="0"
      className={'rt-grid-wrapped'}
    >
      <DataGridProvider state={{ ...parentProps, selected, isGetPending, toolbarActionsComponent, isDataChanged, data }}>
        {validationAlert}
        {contextMenu}

        <Grid
          ref={gridTestRef}
          scrollable={scrollable || "scrollable"}
          columnVirtualization={columnVirtualization}
          className={"rt-grid " + (gridSize ? gridSize.class : 'grid-medium') + " " + propsClassName}
          data={gridData}
          detail={detail}
          onRowDoubleClick={onDoubleClick}
          dataItemKey={dataPrimaryField}
          showLoader={isLoading || initLoading}
          loader={loader}
          edit={edit}
          editable={editable}
          onEditChange={onEditChange}
          detailExpand={detailExpand}
          onItemChange={onItemChange}
          filter={gridFilter}
          filterOperators={FILTER_OPERATORS}
          onFilterChange={onFilterChange}
          filterable={filterable}
          selectedField="selected"
          select={gridSelected}
          onSelectionChange={onSelectChange}
          onHeaderSelectionChange={onHeaderSelectionChange}
          selectable={selectable}
          pageable={isHiddenORDisabled(flags.PAGING) ? false : pageable}
          onPageChange={onPageChange}
          take={page?.take}
          skip={page?.skip}
          rowHeight={rowWidth}
          total={total}
          resizable={true}
          onColumnResize={onColumnResize}
          onDetailExpandChange={onExpandChange}
          reorderable
          onColumnReorder={onColumnReorder}
          onSortChange={onSortChange}
          sort={isHiddenORDisabled(flags.SORT) ? null : sort}
          sortable={sortable}
          style={{ ...style, width: '100%', height: '100%' }}
          groupable={args?.groupable}
          lockGroups={args?.lockGroups}
          onGroupChange={handleGroupChange}
          group={group}
          rows={rows}
          contextMenu={!!contextMenu}
          navigatable={navigatable}
          clipboard={navigatable}
        >
          {gridChildren}
        </Grid>

        {isOpenDelete && <ConfirmDialog
          isOpen={isOpenDelete}
          onClose={handleCloseDelete}
          onConfirm={handleConfirmDelete}
          text={buildDeleteCancelText(selected, false)}
        />}
        {isOpenCancel && <ConfirmDialog
          isOpen={isOpenCancel}
          onClose={handleCloseCancel}
          onConfirm={handleConfirmCancel}
          text={buildDeleteCancelText(selected, true)}
        />}

        {isOpenReset && <ConfirmDialog
          isOpen={isOpenReset}
          onClose={() => setIsOpenReset(false)}
          onConfirm={() => {
            handleReset(resetValue);
            setIsOpenReset(false);
          }}
          text={buildResetText(resetValue)}
        />}

        {isOpenSortWarning && <Dialog
          isOpen={isOpenSortWarning}
          onClose={handleCloseSortWarning}
          onConfirm={handleConfirmSortWarning}
          title="Upozornenie"
          hideCancelBtn={true}
        >
          <div className='p-3 d-flex justify-content-center'>
            <div>
              <p>Zoraďovaním údajov vo viac ako štyroch stĺpcoch môže nastať spomalenie systému</p>
              <Checkbox
                name="sortWarning"
                onChange={() => setSortWarningChecked((prev) => !prev)}
                value={sortWarningChecked}
                label="Viac toto upozornenie nezobrazovať"
              />
            </div>
          </div>
        </Dialog>}

        {selectedFile !== null && <FileDialog
          isOpen={selectedFile !== null}
          onClose={() => setSelectedFilte(null)}
          dataItem={selectedFile}
          column={_column}
        />}

        {isOpenFilter && <FilterDialog
          schema={columns}
          isOpen={isOpenFilter}
          handleCloseFilter={() => setIsOpenFilter(false)}
          handleConfirmFilter={handleConfirmFilter}
          enumDefault={ENUM_DEFAULT}
          primaryField={primaryField}
          parentProps={parentProps}
          frms={frms}
          userFilter={!isHidden(flags.USER_FILTER)}
          userFilterDisabled={isDisabled(flags.USER_FILTER)}
          userFilterList={userFilterExtended}
          getFilterList={(iUz) => { handleGetFilters && handleGetFilters(primaryField, columns, iUz); }}
          createFilter={createFilter}
          updateFilter={updateFilter}
          deleteFilter={deleteFilter}
          getSchema={onGetSchema}
          filter={showFilter || FILTER}
          getDefaultUserId={getDefaultUserId}
          managedUsers={managedUsers}
          handleOpenFilter={() => setIsOpenFilter(true)} //odstrániť
          title={"filterTestInput"}
        />}

        {isOpenSort && <DialogSort
          isOpen={isOpenSort}
          handleCloseSort={() => setIsOpenSort(false)}
          handleConfirmSort={handleConfirmSort}
          sort={sort}
          schema={columns.filter((col) => !(col.sortable === false))}
        />}

        {report.isOpen && <ReportDialog
          isOpen={report.isOpen}
          onClose={handleCloseReportDialog}
          blob={report.blob}
          fileName={report.fileName}
        />}

      </DataGridProvider>
    </div>
  );
});

DataGridComponent.propTypes = {
  /** Array of actions for selection Menu */
  actions: PropTypes.array,

  /** Custom argument */
  args: PropTypes.object,

  /** Flag indicating whether to get detail data from grid data */
  allColumns: PropTypes.bool,

  /** Text for the cancel action */
  cancelText: PropTypes.string,

  /** Function to render cells. Note: `cellRender` is implemented in ExtendedDataGrid */
  cellRender: PropTypes.func,

  /** Class name for the grid */
  className: PropTypes.string,

  /** Enables virtualization of the columns. */
  columnVirtualization: PropTypes.bool,

  /** Factory function to create components */
  componentFactory: PropTypes.func,

  /** Context menu element. Note: `contextMenu` is implemented in ExtendedDataGrid */
  contextMenu: PropTypes.element,

  /** Function to create a user filter */
  createFilter: PropTypes.func,

  /** Function to provide custom print properties. */
  customPrintProps: PropTypes.func,

  /** String representing a custom breadcrumb for the toolbar */
  customToolbarBreadcrumb: PropTypes.string,

  /** Array of data to be displayed in the grid */
  data: PropTypes.array.isRequired,

  /** Database connection string */
  db: PropTypes.string,

  /** Function to delete a user filter */
  deleteFilter: PropTypes.func,

  /** Text for the delete action */
  deleteText: PropTypes.string,

  /** Detail component to show on row expand. Note: `onExpandChange` is implemented in MetaGrid */
  detail: PropTypes.any,

  /** Kendo edit object configuration for expansion functionality */
  detailExpand: PropTypes.object,
  
  /** Number indicating disabled actions */
  disabled: PropTypes.number,

  /** Kendo edit object configuration for editing functionality */
  edit: PropTypes.object,

  /** Kendo editable object */
  editable: PropTypes.object,

  /** Array for extended build columns, newly added */
  extendedBuildColumns: PropTypes.array,

  /** Flag to select which filter to use */
  extendedFilter: PropTypes.bool,

  /** Object representing the filter settings */
  filter: PropTypes.object,

  /** Function to render the filter cell */
  filterCellRender: PropTypes.func,

  /** Factory function for creating filters */
  filterFactory: PropTypes.func,

  /** String representing a field used for filtering */
  filterField: PropTypes.string,

  /** Object representing the fixed filter settings */
  fixedFilter: PropTypes.object,

  /** Array of forms */
  frms: PropTypes.array,

  /** Function to select the default user in the `FilterDialog` */
  getDefaultUserId: PropTypes.func,

  /** Function that handles form changes */
  handleChangeFrm: PropTypes.func,

  /** Function to get filters in the `FilterDialog` */
  handleGetFilters: PropTypes.func,

  /** Function to handle key down events, implemented in ExtendedDataGrid */
  handleKeyDown: PropTypes.func,

  /** Function called when the reset button is pressed */
  handleReset: PropTypes.func,

  /** Number indicating hidden actions */
  hidden: PropTypes.number,

  /** Boolean indicating if the back button should be hidden */
  hiddenBack: PropTypes.bool,

  /** Object representing the initial filter settings */
  initFilter: PropTypes.object,

  /** Array representing the initial sort settings */
  initSort: PropTypes.array,

  /** Boolean indicating if the data has changed. Sent from ExtendedDataGrid to the toolbar*/
  isDataChanged: PropTypes.bool,

  /** Boolean indicating if a pending request to get detail data exists */
  isGetPending: PropTypes.bool,

  /** Boolean indicating if data is being loaded */
  isLoading: PropTypes.bool.isRequired,

  /** Function to check if the value has changed */
  isValueChanged: PropTypes.func,

  /** Array of managed users */
  managedUsers: PropTypes.array,

  /** Boolean indicating if multiple row selection is enabled */
  multiple: PropTypes.bool,

  /** Boolean indicating if keyboard navigation is enabled */
  navigatable: PropTypes.bool,

  /** Boolean indicating if metadata is not required for printing */
  noMeta: PropTypes.bool,

  /** Function invoked when all changes are aborted */
  onAbortChangesAll: PropTypes.func,

  /** Function invoked when a new entry is added */
  onAdd: PropTypes.func,

  /** Function invoked when an entry is canceled */
  onCancel: PropTypes.func,

  /** Function invoked on change of filter, sort or page */
  onChange: PropTypes.func,

  /** Function invoked when a field is changed */
  onChangeField: PropTypes.func,

  /** Function invoked when the schema changes */
  onChangeSchema: PropTypes.func,

  /** Function invoked when an entry is copied */
  onCopy: PropTypes.func,

  /** Function invoked when additional copy actions are performed */
  onCopyAdditional: PropTypes.func,

  /** Function invoked when an entry is deleted */
  onDelete: PropTypes.func,

  /** Function invoked when the edit action occurs */
  onEditChange: PropTypes.func,

  /** Function invoked when the expanded state of a row changes - kendo onDetailExpandChange */
  onExpandChange: PropTypes.func,

  /** Function invoked when a filter is applied */
  onFilter: PropTypes.func,

  /** Function invoked to get data (required) */
  onGet: PropTypes.func.isRequired,

  /** Function invoked to get a list of data (required) */
  onGetList: PropTypes.func.isRequired,

  /** Function invoked when a schema is fetched */
  onGetSchema: PropTypes.func,

  /** Function invoked when an item changes */
  onItemChange: PropTypes.func,

  /** Function invoked when the print action is triggered */
  onPrint: PropTypes.func,

  /** Function invoked when refresh button is triggered */
  onRefresh: PropTypes.func,

  /** Function invoked when a row is double-clicked */
  onRowDoubleClick: PropTypes.func,

  /** Function invoked when a row is selected */
  onSelect: PropTypes.func,

  /** Function invoked when all entries are updated */
  onUpdateAll: PropTypes.func,

  /** Object representing pagination settings, e.g., { skip: 0, take: 5 } */
  page: PropTypes.object,

  /** Object containing data for enums and pickers. NOTE: Does not include the full state from the meta grid */
  parentProps: PropTypes.object,

  /** Array representing predefined filters */
  predefinedFilter: PropTypes.array,

  /** Primary field used as a unique identifier */
  primaryField: PropTypes.string.isRequired,

  /** Array of items to be printed */
  printItems: PropTypes.array,

  /** Reducer function used for toolbar printing */
  reducer: PropTypes.func,

  /** Boolean indicating if the selection should be remembered when navigating pages, change filter and sort */
  rememberSelection: PropTypes.bool,

  /** Function to provide additional props to grid rows */
  rowProps: PropTypes.func,

  /** Array of objects representing the schema of the grid */
  schema: PropTypes.array,

  /** String indicating scrolling mode */
  scrollable: PropTypes.string,

  /** Array of selected entries */
  selected: PropTypes.array,

  /** Object representing the selected form */
  selectedFrm: PropTypes.object,

  /** Boolean indicating if the abort button should be shown for async requests */
  showAbort: PropTypes.bool,

  /** Boolean indicating if toolbar buttons should be displayed during editing  */
  showToolbarButtons: PropTypes.bool,

  /** Array representing the current sort state */
  sort: PropTypes.array,

  /** Object containing custom styles */
  style: PropTypes.object,

  /** Object representing metadata for reports */
  tlac: PropTypes.object,

  /** Actions available in the toolbar, which can be a function, an array, or an element type */
  toolbarActions: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.array,
    PropTypes.elementType
  ]),

  /** Number defining the total number of data items across all pages */
  total: PropTypes.number,

  /** Function that updates user filters */
  updateFilter: PropTypes.func,

  /** Function to update the sort state */
  updateSort: PropTypes.func,

  /** Array displayed to the user in FilterDialog, can include filters for "managedUsers" */
  userFilterExtended: PropTypes.array,

  /** Array of user filters for the currently logged-in user */
  userFilterList: PropTypes.array,

  /** Validation alert element, created in ExtendedDataGrid */
  validationAlert: PropTypes.element,

  /** Determines if metadata should be used in editPickerCell */
  withMeta: PropTypes.bool,
};


export default memo(DataGridComponent);
